/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import {error} from '@ion/core/src/logger/logger';
import {
  loadDeviceType,
  loadDeviceTypes
} from '@ion/core/src/services/Connect';
import {
  AuditChange,
  DeviceType,
  DeviceTypesFilters
} from '@ion/core/src/types/entities';
import {LoadDeviceTypesParams} from '@ion/core/src/types/services';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {PageInitializeAction} from '@/types/redux';

// name
export const NAME                                   = 'devicetypes';

// initialise the grid
export const INITIALIZE_DEVICE_TYPES                = 'INITIALIZE_DEVICE_TYPES';
export const SET_GRID_COLUMNS                       = 'SET_DEVICE_TYPES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                    = 'UPDATE_DEVICE_TYPES_GRID_COLUMNS';

// refresh grid ...
export const DEVICE_TYPES_GRID_REFRESHED            = 'DEVICE_TYPES_GRID_REFRESHED';

export const SET_DEVICE_TYPES_GRID_PAGE_SIZE                     = 'SET_DEVICE_TYPES_GRID_PAGE_SIZE';

// retrieve Device Types
export const GET_DEVICE_TYPES_REQUEST               = 'GET_DEVICE_TYPES_REQUEST';
export const GET_DEVICE_TYPES_RESPONSE              = 'GET_DEVICE_TYPES_RESPONSE';
export const GET_DEVICE_TYPES_ERROR                 = 'GET_DEVICE_TYPES_ERROR';

// View device types
export const VIEW_DEVICE_TYPES                      = 'VIEW_DEVICE_TYPES';

// Audit device types
export const AUDIT_DEVICE_TYPES                     = 'AUDIT_DEVICE_TYPES';
export const AUDIT_DEVICE_TYPES_SHOW_CHANGES_DIALOG = 'AUDIT_DEVICE_TYPES_SHOW_CHANGES_DIALOG';

// search queries
export const UPDATE_DEVICE_TYPES_SEARCH_QUERIES     = 'UPDATE_DEVICE_TYPES_SEARCH_QUERIES';
export const RESET_DEVICE_TYPES_SEARCH_QUERIES      = 'RESET_DEVICE_TYPES_SEARCH_QUERIES';

// add
export const ADD_DEVICE_TYPE                        = 'ADD_DEVICE_TYPE';

/**
 * @initialize
 */
export const initializeDeviceType = (): PageInitializeAction => {
  return {type: INITIALIZE_DEVICE_TYPES};
};

// Updates the modular search queries for Admin AEMPConfig
export const updateDeviceTypesSearchQueries = (searchParams: DeviceTypesFilters) => ({
  type : UPDATE_DEVICE_TYPES_SEARCH_QUERIES, searchParams
});

// Resets the Peg Labels filters state
export const resetDeviceTypesSearchQueries = () => ({
  type : RESET_DEVICE_TYPES_SEARCH_QUERIES
});

// Toggles the Device Type Add editor
export const toggleEditor = (show = true) => ({
  type : ADD_DEVICE_TYPE, show
});

/**
 * Invoked when the the Device Types auditor is to be displayed or removed.
 */
export const auditDeviceTypes = (deviceType: DeviceType, show = true) => ({
  type : AUDIT_DEVICE_TYPES, deviceType, show
});

export const showDeviceTypesAuditChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_DEVICE_TYPES_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * @getDeviceTypes
 *
 * fetches device types for this account to be displayed in the Devices
 * section
 */
export const getDeviceTypes = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_DEVICE_TYPES_REQUEST});
    const {
      devicetypes: {
        search
      }
    } = getState();
    const data: LoadDeviceTypesParams = {
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...createModularSearchFilters(search)
    };
    try {
      const {items, totalCount} = !isEmpty(data.filterOn)
        ? await loadDeviceType(data)
        : await loadDeviceTypes(data);
      dispatch({type: GET_DEVICE_TYPES_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_DEVICE_TYPES_ERROR, error: err});
      return error(err);
    }
  };
};

/**
 * Toggles the View Device Type Dialog
 */
export const toggleViewDeviceTypeDialog = (deviceType: DeviceType, show = true) => ({
  type : ADD_DEVICE_TYPE, deviceType, show
});

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setDeviceTypesGridPageSize = (pageSize: number) => ({
  type : SET_DEVICE_TYPES_GRID_PAGE_SIZE, pageSize
});
